
export interface User {
  id: string;
  discordId: string;
  username: string;
  discriminator: string | null;
  avatar: string | null;
  email: string | null;
  createdAt: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export class AuthService {
  private static instance: AuthService;
  private user: User | null = null;
  private listeners: Set<(state: AuthState) => void> = new Set();
  private isRestoringSession = false;
  private lastRestoreAttempt = 0;
  private readonly RESTORE_COOLDOWN = 5000; // 5 seconds cooldown between restore attempts

  private constructor() {
    // Listen for logout events
    window.addEventListener('auth:logout', this.handleLogout.bind(this));
    
    // Try to restore session on init (only once)
    this.restoreSession();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private notifyListeners() {
    const state: AuthState = {
      user: this.user,
      isAuthenticated: !!this.user,
      isLoading: false,
    };
    this.listeners.forEach(listener => listener(state));
  }

  subscribe(listener: (state: AuthState) => void) {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener({
      user: this.user,
      isAuthenticated: !!this.user,
      isLoading: false,
    });
    return () => this.listeners.delete(listener);
  }

  async restoreSession(): Promise<User | null> {
    // Prevent concurrent restore attempts
    if (this.isRestoringSession) {
      return this.user;
    }

    // Rate limit restore attempts
    const now = Date.now();
    if (now - this.lastRestoreAttempt < this.RESTORE_COOLDOWN) {
      return this.user;
    }

    this.isRestoringSession = true;
    this.lastRestoreAttempt = now;

    try {
      // Check if we have a token in localStorage
      const { api } = await import('./api');
      const token = api.getToken();
      if (!token) {
        this.user = null;
        this.notifyListeners();
        return null;
      }

      // Verify token by getting current user
      try {
        const { authApi } = await import('./api');
        const user = await authApi.getMe();
        this.user = user;
        const { api } = await import('./api');
        api.setToken(token);
        this.notifyListeners();
        return this.user;
      } catch (error: any) {
        // Token is invalid, clear it
        // Don't log if it's a rate limit error (429), auth error (403/401), or resource exhaustion to avoid spam
        if (error?.status !== 429 && 
            error?.status !== 403 && 
            error?.status !== 401 && 
            !error?.resourceExhausted && 
            !error?.circuitBreaker) {
          console.error('Token validation failed:', error);
        }
        this.user = null;
        const { api } = await import('./api');
        api.setToken(null);
        this.notifyListeners();
        return null;
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      this.user = null;
      const { api } = await import('./api');
      api.setToken(null);
      this.notifyListeners();
      return null;
    } finally {
      this.isRestoringSession = false;
    }
  }

  private isLoggingIn = false;
  private lastLoginAttempt = 0;
  private readonly LOGIN_COOLDOWN = 2000; // 2 seconds cooldown between login attempts
  private isLoggingOut = false;

  async login(token?: string): Promise<User> {
    // Prevent concurrent login attempts
    if (this.isLoggingIn) {
      // If already logging in, wait for the existing attempt
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!this.isLoggingIn) {
            clearInterval(checkInterval);
            if (this.user) {
              resolve(this.user);
            } else {
              reject(new Error('Login attempt failed'));
            }
          }
        }, 100);
        
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Login timeout'));
        }, 5000);
      });
    }

    // Rate limit login attempts
    const now = Date.now();
    if (now - this.lastLoginAttempt < this.LOGIN_COOLDOWN) {
      if (this.user) {
        return this.user;
      }
      throw new Error('Please wait a moment before trying again.');
    }

    this.isLoggingIn = true;
    this.lastLoginAttempt = now;

    try {
      // If token is provided (from callback), set it and verify
      if (token) {
        const { api } = await import('./api');
        api.setToken(token);
        
        try {
          // Verify token by getting current user
          const { authApi } = await import('./api');
          const user = await authApi.getMe();
          this.user = user;
          this.notifyListeners();
          return this.user;
        } catch (error: any) {
          const { api } = await import('./api');
          api.setToken(null);
          
          // Handle specific error types
          if (error?.status === 403 || error?.status === 401) {
            // 403 Forbidden or 401 Unauthorized - token is invalid/expired
            // Token already cleared by API client, just throw user-friendly error
            throw new Error('Session expired. Please login again.');
          } else if (error?.circuitBreaker || error?.resourceExhausted) {
            // Circuit breaker is open or resources exhausted
            throw new Error(
              error.message || 'Service temporarily unavailable. Please refresh the page in a moment.'
            );
          } else if (error?.status === 429) {
            throw new Error('Too many requests. Please wait a moment and try again.');
          } else if (error?.status === 503) {
            throw new Error('Service temporarily unavailable. Please try again later.');
          } else if (error?.message?.includes('Failed to fetch') || 
                     error?.message?.includes('insufficient resources') ||
                     error?.message?.includes('NetworkError')) {
            throw new Error('Network error. Please check your connection and try again.');
          } else {
            // Don't log if it's a rate limit error (429) or auth error (403/401) to avoid spam
            if (error?.status !== 429 && error?.status !== 403 && error?.status !== 401) {
              console.error('Token validation failed:', error);
            }
            throw new Error('Invalid token. Please login again.');
          }
        }
      }

      // Otherwise, restore from current session
      const session = await this.restoreSession();
      if (session) {
        return session;
      }

      throw new Error('No session found');
    } finally {
      this.isLoggingIn = false;
    }
  }

  async logout(): Promise<void> {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    try {
      const { api } = await import('./api');
      const token = api.getToken();

      // Call backend logout endpoint only if a token exists
      if (token) {
        const { authApi } = await import('./api');
        await authApi.logout().catch(() => {
          // Ignore errors, logout client-side anyway
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.user = null;
      const { api } = await import('./api');
      api.setToken(null);
      this.notifyListeners();
      this.isLoggingOut = false;
    }
  }

  private handleLogout() {
    (async () => {
      const { api } = await import('./api');
      if (!api.getToken() && !this.user) {
        return;
      }
      this.logout();
    })();
  }

  getUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.user;
  }

  async getDiscordAuthUrl(): Promise<string> {
    // Get Discord OAuth URL from backend
    const { authApi } = await import('./api');
    return await authApi.getDiscordAuthUrl();
  }

  async signInWithDiscord(): Promise<void> {
    try {
      console.log('Initiating Discord login...');
      // Get Discord OAuth URL and redirect
      const authUrl = await this.getDiscordAuthUrl();
      
      if (!authUrl || authUrl === 'undefined' || authUrl.includes('undefined')) {
        throw new Error('Invalid Discord OAuth URL generated. Please check configuration.');
      }
      
      console.log('Redirecting to Discord OAuth:', authUrl);
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Error in signInWithDiscord:', error);
      throw error;
    }
  }
}

export const authService = AuthService.getInstance();
